import { useEffect, useState } from 'react';
import type { Bridge } from '../../contracts/bridge';
import { apiClient } from '../../core/apiClient';

interface OverviewPanelProps {
  bridge: Bridge;
}

interface ForecastSummary {
  bridge_name: string;
  proof_id: string;
  timesteps: number;
  min_deck_temp_c: number;
  max_deck_temp_c: number;
  max_ice_thickness_mm: number;
  ice_hours: number;
  model_version: string;
  ice_window: { start: string; end: string } | null;
  treatment_window: { start: string; end: string } | null;
}

export function OverviewPanel({ bridge }: OverviewPanelProps) {
  const [summary, setSummary] = useState<ForecastSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadForecast();
  }, [bridge.bridge_id]);

  const loadForecast = async () => {
    setIsLoading(true);
    setError(null);
    console.log('[Forecast] Loading for bridge:', bridge.bridge_id);
    try {
      const response = await apiClient.post<ForecastSummary>('/engine/forecast', {
        bridge_id: bridge.bridge_id,
        requested_by: 'ui',
      });
      console.log('[Forecast] Response:', response);
      setSummary(response);
    } catch (err) {
      console.error('[Forecast] Error:', err);
      setError('Failed to load forecast: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="panel-header">
        <h3 className="panel-title">Overview</h3>
        <button className="btn btn--secondary btn--sm" onClick={loadForecast}>
          Refresh
        </button>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center p-4">
          <div className="loading-spinner" />
        </div>
      )}

      {error && (
        <div className="p-4 text-sm text-error">
          {error}
        </div>
      )}

      {summary && !isLoading && (
        <div className="flex flex-col gap-4">
          <div className="card">
            <div className="text-sm text-secondary mb-1">Deck Temperature Range</div>
            <div className="text-xl font-semibold">
              {summary.min_deck_temp_c.toFixed(1)}°C to {summary.max_deck_temp_c.toFixed(1)}°C
            </div>
          </div>

          <div className="card">
            <div className="text-sm text-secondary mb-1">Max Ice Thickness</div>
            <div className="text-xl font-semibold">
              {summary.max_ice_thickness_mm.toFixed(1)} mm
            </div>
          </div>

          <div className="card">
            <div className="text-sm text-secondary mb-1">Ice Hours</div>
            <div className="text-xl font-semibold">{summary.ice_hours} hours</div>
          </div>

          {summary.ice_window && (
            <div className="card">
              <div className="text-sm text-secondary mb-1">Ice Window</div>
              <div className="text-sm">
                {new Date(summary.ice_window.start).toLocaleString()} –{' '}
                {new Date(summary.ice_window.end).toLocaleString()}
              </div>
            </div>
          )}

          <div className="text-xs text-secondary mt-2">
            Model: {summary.model_version} | Proof: {summary.proof_id.slice(0, 8)}...
          </div>
        </div>
      )}
    </div>
  );
}
